import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  CheckCheck,
  FileText,
  Pencil,
  Receipt,
  Trash2,
} from "lucide-react";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { formatMoney } from "@/lib/currencyFormat";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDisplayDate } from "@/lib/dateFormat";
import { expenseKeys, supplierKeys } from "@/lib/queryKeys";
import { listExpenses } from "@/services/expensesService";
import { listRecibidas } from "@/services/invoiceService";
import {
  deleteSupplier,
  getSupplier,
  updateSupplier,
} from "@/services/supplierService";
import { SupplierSummaryTab } from "@/pages/suppliers/SupplierSummaryTab";

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  RUT: "RUT",
  CI: "CI",
  EXT: "EXT",
};

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[minmax(0,140px)_1fr] gap-3 py-2 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,180px)_1fr]">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value ?? "—"}</dd>
    </div>
  );
}

const TAB_SUMMARY = "summary";
const TAB_DETAILS = "details";
const TAB_ACTIVITY = "activity";

export function SupplierDetailsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const tabParam = searchParams.get("tab");
  const activeTab =
    tabParam === TAB_ACTIVITY
      ? TAB_ACTIVITY
      : tabParam === TAB_DETAILS
        ? TAB_DETAILS
        : TAB_SUMMARY;

  const setTab = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === TAB_SUMMARY) next.delete("tab");
        else next.set("tab", value);
        return next;
      },
      { replace: true },
    );
  };

  const {
    data: supplier,
    isLoading,
    isError,
  } = useQuery({
    queryKey: supplierKeys.detail(id ?? ""),
    queryFn: () => getSupplier(id!),
    enabled: Boolean(id),
  });

  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  const formatYmd = (d: Date): string =>
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0");

  const {
    data: recibidas,
    isLoading: recibidasLoading,
  } = useQuery({
    queryKey: ["invoices", "recibidas", "by-supplier", id ?? ""],
    queryFn: () =>
      listRecibidas({
        dateFrom: formatYmd(oneYearAgo),
        dateTo: formatYmd(today),
        limit: 100,
        supplierId: id,
      }),
    enabled: Boolean(id) && activeTab === TAB_ACTIVITY,
  });

  const {
    data: expensesData,
    isLoading: expensesLoading,
  } = useQuery({
    queryKey: expenseKeys.list({
      page: 1,
      limit: 100,
      supplierId: id ?? undefined,
    }),
    queryFn: () => listExpenses({ page: 1, limit: 100, supplierId: id }),
    enabled: Boolean(id) && activeTab === TAB_ACTIVITY,
  });

  const toggleAutoAck = useMutation({
    mutationFn: (next: boolean) =>
      updateSupplier(id!, { autoAckCfeOnReceive: next }),
    onSuccess: (updated) => {
      queryClient.setQueryData(supplierKeys.detail(updated.id), updated);
      toast.success(
        updated.autoAckCfeOnReceive
          ? t("suppliers.detail.autoAckOnToast", "Aceptación automática activada.")
          : t(
              "suppliers.detail.autoAckOffToast",
              "Aceptación automática desactivada.",
            ),
      );
    },
    onError: () => {
      toast.error(
        t(
          "suppliers.detail.autoAckErrorToast",
          "No pudimos actualizar la configuración.",
        ),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSupplier(id!),
    onSuccess: () => {
      toast.success(t("suppliers.detail.deleted", "Proveedor eliminado."));
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      navigate("/suppliers");
    },
    onError: () => {
      toast.error(
        t(
          "suppliers.detail.deleteError",
          "No pudimos eliminar el proveedor.",
        ),
      );
    },
  });

  if (!id) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("suppliers.detail.invalidId", "Proveedor inválido")}
          onBack={() => navigate("/suppliers")}
        />
      </div>
    );
  }

  if (isLoading) return <LoadingPage />;

  if (isError || !supplier) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("suppliers.detail.title", "Proveedor")}
          onBack={() => navigate("/suppliers")}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          {t("suppliers.detail.notFound", "Proveedor no encontrado.")}
        </p>
      </div>
    );
  }

  const recibidasList = recibidas?.data ?? [];
  const expensesList = expensesData?.data ?? [];
  const activityLoading = recibidasLoading || expensesLoading;

  type ActivityRow = {
    kind: "recibida" | "expense";
    id: string;
    date: string;
    label: string;
    currency: string;
    total: number;
    linkTo: string;
  };
  const mergedActivity: ActivityRow[] = [
    ...recibidasList.map<ActivityRow>((r) => {
      const typeLabel = r.tipoCFE
        ? t(`invoices.cfeTypes.${r.tipoCFE}`, r.tipoCFE)
        : "";
      const serie = r.serie?.trim() ?? "";
      const numeric = r.nro != null ? String(r.nro) : "";
      const trailing = [serie, numeric].filter(Boolean).join("-");
      const label = trailing
        ? `${typeLabel} #${trailing}`.trim()
        : typeLabel || "—";
      return {
        kind: "recibida" as const,
        id: r.id,
        date: r.fechaHora?.slice(0, 10) ?? r.createdAt?.slice(0, 10) ?? "",
        label,
        currency: r.moneda || "UYU",
        total: r.montoTotal,
        linkTo: `/invoices/recibidas/${r.id}`,
      };
    }),
    ...expensesList.map<ActivityRow>((e) => ({
      kind: "expense" as const,
      id: e.id,
      date: e.occurredAt,
      label: e.category?.trim() || t("suppliers.detail.activity.uncategorized", "Sin categoría"),
      currency: e.currency || "UYU",
      total: parseFloat(e.total),
      linkTo: `/expenses/${e.id}`,
    })),
  ].sort((a, b) => b.date.localeCompare(a.date));

  const displayNameTrimmed = supplier.displayName?.trim();
  const usingCustomDisplayName =
    Boolean(displayNameTrimmed) && displayNameTrimmed !== supplier.legalName;
  const taxIdValue =
    supplier.documentType === "EXT"
      ? supplier.docRecepExt?.trim() || null
      : supplier.rut?.trim() || null;
  const taxIdLabel = taxIdValue
    ? `${supplier.documentType === "EXT" ? supplier.documentType : "RUT"} ${taxIdValue}`
    : null;
  const headerSubtitle = usingCustomDisplayName
    ? [supplier.legalName, taxIdLabel].filter(Boolean).join(" · ")
    : taxIdLabel ?? null;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={displayNameTrimmed || supplier.legalName}
        description={headerSubtitle || undefined}
        onBack={() => navigate("/suppliers")}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/suppliers/${id}/edit`)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t("suppliers.detail.edit", "Editar")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("suppliers.detail.delete", "Eliminar")}
            </Button>
          </div>
        }
      />

      <Tabs value={activeTab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value={TAB_SUMMARY}>
            {t("suppliers.detail.tabs.summary", "Resumen")}
          </TabsTrigger>
          <TabsTrigger value={TAB_DETAILS}>
            {t("suppliers.detail.tabs.details", "Detalles")}
          </TabsTrigger>
          <TabsTrigger value={TAB_ACTIVITY}>
            {t("suppliers.detail.tabs.activity", "Actividad")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={TAB_SUMMARY} className="mt-0 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t(
                  "suppliers.detail.ackCard.title",
                  "Aceptación automática",
                )}
              </CardTitle>
              <CardDescription>
                {t(
                  "suppliers.detail.ackCard.description",
                  "Cuando este proveedor envía un EnvioCFE entre empresas, ¿lo aceptamos automáticamente?",
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/20 p-4">
                <Checkbox
                  id="supplier-detail-auto-ack"
                  checked={supplier.autoAckCfeOnReceive}
                  disabled={toggleAutoAck.isPending}
                  onCheckedChange={(checked) =>
                    toggleAutoAck.mutate(checked === true)
                  }
                  className="mt-0.5 h-5 w-5"
                />
                <div className="space-y-1">
                  <div className="text-sm font-medium text-foreground">
                    {supplier.autoAckCfeOnReceive
                      ? t(
                          "suppliers.detail.ackCard.statusOn",
                          "Activada — aceptamos sin pedir confirmación",
                        )
                      : t(
                          "suppliers.detail.ackCard.statusOff",
                          "Desactivada — tenés que aceptar manualmente cada comprobante",
                        )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "suppliers.detail.ackCard.hint",
                      "Sólo aplica a comprobantes recibidos por correo (EnvioCFE entre empresas), no a los importados desde DGI.",
                    )}
                  </p>
                </div>
              </label>
            </CardContent>
          </Card>

          <SupplierSummaryTab supplierId={id} />
        </TabsContent>

        <TabsContent value={TAB_DETAILS} className="mt-0 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t("suppliers.form.identity", "Identidad")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border">
                <DetailRow
                  label={t("suppliers.form.legalName", "Razón social")}
                  value={supplier.legalName}
                />
                {supplier.displayName?.trim() ? (
                  <DetailRow
                    label={t(
                      "suppliers.form.displayName",
                      "Nombre comercial",
                    )}
                    value={supplier.displayName}
                  />
                ) : null}
                <DetailRow
                  label={t("suppliers.form.documentType", "Tipo de documento")}
                  value={
                    DOCUMENT_TYPE_LABELS[supplier.documentType] ??
                    supplier.documentType
                  }
                />
                <DetailRow
                  label={t("suppliers.form.rut", "RUT / Documento")}
                  value={
                    supplier.documentType === "EXT"
                      ? supplier.docRecepExt?.trim() || "—"
                      : supplier.rut?.trim() || "—"
                  }
                />
                {supplier.countryCode?.trim() ? (
                  <DetailRow
                    label={t("suppliers.form.country", "País")}
                    value={supplier.countryCode}
                  />
                ) : null}
                {supplier.address?.trim() ? (
                  <DetailRow
                    label={t("suppliers.form.address", "Dirección")}
                    value={supplier.address}
                  />
                ) : null}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t("suppliers.form.contact", "Contacto y ubicación")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border">
                <DetailRow
                  label={t("suppliers.form.email", "Email")}
                  value={supplier.email?.trim() || "—"}
                />
                <DetailRow
                  label={t("suppliers.form.phone", "Teléfono")}
                  value={supplier.phone?.trim() || "—"}
                />
                {supplier.ciudad?.trim() ? (
                  <DetailRow
                    label={t("suppliers.form.ciudad", "Ciudad")}
                    value={supplier.ciudad}
                  />
                ) : null}
                {supplier.departamento?.trim() ? (
                  <DetailRow
                    label={t(
                      "suppliers.form.departamento",
                      "Departamento",
                    )}
                    value={supplier.departamento}
                  />
                ) : null}
                {supplier.dgiNotificationEmail?.trim() ? (
                  <DetailRow
                    label={t(
                      "suppliers.detail.dgiNotificationEmail",
                      "Email DGI para confirmaciones",
                    )}
                    value={supplier.dgiNotificationEmail}
                  />
                ) : null}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t("suppliers.form.preferences", "Preferencias")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border">
                <DetailRow
                  label={t(
                    "suppliers.form.defaultCurrency",
                    "Moneda por defecto",
                  )}
                  value={supplier.defaultCurrency ?? "—"}
                />
                <DetailRow
                  label={t(
                    "suppliers.form.defaultCategory",
                    "Categoría de gasto",
                  )}
                  value={supplier.defaultCategory?.trim() || "—"}
                />
                <DetailRow
                  label={t(
                    "suppliers.form.autoAck",
                    "Aceptar automáticamente comprobantes recibidos",
                  )}
                  value={
                    supplier.autoAckCfeOnReceive ? (
                      <StatusBadge
                        variant="success"
                        className="inline-flex items-center gap-1"
                      >
                        <CheckCheck className="h-3 w-3" aria-hidden />
                        <span>
                          {t("suppliers.detail.autoAckOn", "Activada")}
                        </span>
                      </StatusBadge>
                    ) : (
                      <span className="text-muted-foreground">
                        {t("suppliers.detail.autoAckOff", "Desactivada")}
                      </span>
                    )
                  }
                />
                {supplier.notes?.trim() ? (
                  <DetailRow
                    label={t("suppliers.form.notes", "Notas")}
                    value={
                      <pre className="whitespace-pre-wrap break-words text-sm text-foreground">
                        {supplier.notes}
                      </pre>
                    }
                  />
                ) : null}
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={TAB_ACTIVITY} className="mt-0 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t(
                  "suppliers.detail.activity.title",
                  "Actividad combinada",
                )}
              </CardTitle>
              <CardDescription>
                {t(
                  "suppliers.detail.activity.hint",
                  "Comprobantes electrónicos recibidos y gastos manuales para este proveedor en el último año, ordenados por fecha.",
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="flex min-h-[120px] items-center justify-center">
                  <LoadingLogo size="sm" />
                </div>
              ) : mergedActivity.length === 0 ? (
                <EmptyState
                  title={t(
                    "suppliers.detail.activity.empty",
                    "Todavía no registramos actividad para este proveedor.",
                  )}
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t("suppliers.detail.activity.colDate", "Fecha")}
                      </TableHead>
                      <TableHead>
                        {t("suppliers.detail.activity.colKind", "Tipo")}
                      </TableHead>
                      <TableHead>
                        {t(
                          "suppliers.detail.activity.colDescription",
                          "Detalle",
                        )}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("suppliers.detail.activity.colTotal", "Total")}
                      </TableHead>
                      <TableHead aria-hidden className="w-[40px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mergedActivity.map((row) => (
                      <TableRow
                        key={`${row.kind}-${row.id}`}
                        onRowClick={() => navigate(row.linkTo)}
                      >
                        <TableCell className="text-muted-foreground">
                          {row.date
                            ? formatDisplayDate(row.date, {
                                locale: i18n.language,
                                useCalendarDate: true,
                              })
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {row.kind === "recibida" ? (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <FileText className="h-3.5 w-3.5" aria-hidden />
                              {t(
                                "suppliers.detail.activity.kindRecibida",
                                "Recibida",
                              )}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <Receipt className="h-3.5 w-3.5" aria-hidden />
                              {t(
                                "suppliers.detail.activity.kindExpense",
                                "Gasto",
                              )}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.label}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.currency} {formatMoney(row.total)}
                        </TableCell>
                        <TableCell aria-hidden />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t(
          "suppliers.detail.deleteConfirmTitle",
          "¿Eliminar proveedor?",
        )}
        description={t(
          "suppliers.detail.deleteConfirmDescription",
          "Los comprobantes recibidos y los gastos van a seguir visibles, pero la aceptación automática deja de aplicar. Podés volver a crear el proveedor si lo necesitás.",
        )}
        confirmText={t("suppliers.detail.delete", "Eliminar")}
        cancelText={t("suppliers.form.cancel", "Cancelar")}
        loadingText={t("suppliers.detail.deleting", "Eliminando…")}
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}
